import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

import { createOwnFinancingTableConfig, TableRowAction } from './own-financing-table.config';
import {
  FINANCING_SHORT_TERM,
  FinancingDetailAction,
  FinancingDetailDeleter,
  FinancingDetailLoader,
  financingTermLabel,
  isFinancingAmortized,
} from '../../../financing-detail/financing-detail.model';
import { FinancingFormDialogComponent } from '../../../financing-detail/financing-form-dialog/financing-form-dialog.component';

import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';

import { FinanciacionPropiaService } from '../../../../../modules/finanzas/financiacion-propia/financiacion-propia.service';
import { FinanciacionPropiaModel } from '../../../../../modules/finanzas/financiacion-propia/financiacion-propia.model';
import { FinanciacionPropiaDialogAttachmentComponent } from '../../../../../modules/finanzas/financiacion-propia/financiacion-propia-dialog-attachment.component';
import { FinanciacionPropiaDialogPagoCuotasComponent } from '../../../../../modules/finanzas/financiacion-propia/financiacion-propia-dialog-pago-cuotas.component';
import { FinanciacionPropiaDialogDeudaACortoPlazoComponent } from '../../../../../modules/finanzas/financiacion-propia/financiacion-propia-dialog-deuda-a-corto-plazo.component';
import { FinanciacionPropiaDialogConstitucionComponent } from '../../../../../modules/finanzas/financiacion-propia/financiacion-propia-dialog-constitucion.component';
import { OwnFinancingReadService } from '../../services/own-financing-read.service';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { AccountingManualFormComponent } from '../../../../../modules/accounting/manual/accounting-manual-form.component';

type OwnFinancingRow = FinanciacionPropiaModel & {
  estado: string;
  __expanded?: boolean;
  __detailVersion?: number;
};

@Component({
  standalone: false,
  selector: 'app-own-financing-list',
  templateUrl: './own-financing-list.components.html',
  styleUrls: ['./own-financing-list.components.css']
})
export class OwnFinancingListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('ownFinancingSummary') summaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance';
  title = 'Financiación propia';
  subTitle = 'Consulta, edita o gestiona tu financiación propia';

  quickSearch = '';
  totals = { initial: 0, pending: 0, count: 0 };

  detailLoader: FinancingDetailLoader = (uid: string) => Promise.all([
    this.financiacionPropiaService.constitucionList(uid),
    this.financiacionPropiaService.pagoCoutasList(uid),
    this.financiacionPropiaService.deudaACortoPlazoList(uid),
  ]).then(([constituciones, pagos, deudas]) => ({
    constituciones: (constituciones || []) as any[],
    pagos: (pagos || []) as any[],
    deudas: (deudas || []) as any[],
  }));

  detailDeleter: FinancingDetailDeleter = (operation, uid) => {
    if (operation === 'constitution') return this.financiacionPropiaService.constitucionDelete(uid);
    if (operation === 'shortTermDebt') return this.financiacionPropiaService.deudaACortoPlazoDelete(uid);
    return this.financiacionPropiaService.pagoCoutasDelete(uid);
  };

  private rows: OwnFinancingRow[] = [];
  private loadVersion = 0;
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;
  private readonly quickSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private financiacionPropiaService: FinanciacionPropiaService,
    private reads: OwnFinancingReadService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.tableConfig = createOwnFinancingTableConfig(
      {
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.PayInstallments]: (row) => this.payInstallments(row),
          [TableRowAction.Constitution]: (row) => this.constitution(row),
          [TableRowAction.ShortTermDebt]: (row) => this.shortTermDebt(row),
          [TableRowAction.UploadFile]: (row) => this.uploadFileAttachment(row),
          [TableRowAction.DownloadFile]: (row) => this.downloadFileAttachment(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
        },
        selectable: false,
        columns: [
          { key: 'descripcion', label: 'Descripción', sortable: true, sortKey: 'Descripcion', ellipsis: true, alwaysVisible: true, onCellClick: (row: OwnFinancingRow) => this.edit(row) },
          { key: 'partnersName', label: 'Socio', sortable: true, sortKey: 'PartnersName', ellipsis: true },
          {
            key: 'duracion', label: 'Plazo', sortable: true, sortKey: 'Duracion',
            badge: {
              variantFn: (value: number) => value === FINANCING_SHORT_TERM ? 'info' : 'neutral',
              labelFn: (value: number) => financingTermLabel(value),
            },
          },
          { key: 'importeInicial', label: 'Importe inicial', sortable: true, sortKey: 'ImporteInicial', formatFn: (val) => Utils.FormatMoney(val ?? 0) + ' €' },
          { key: 'importePendiente', label: 'Importe pendiente', sortable: true, sortKey: 'ImportePendiente', formatFn: (val) => Utils.FormatMoney(val ?? 0) + ' €' },
          {
            key: 'estado', label: 'Estado', sortable: true, sortKey: 'Estado',
            badge: {
              variantFn: (_: string, row: OwnFinancingRow) => isFinancingAmortized(row) ? 'success' : 'warning',
              labelFn: (value: string) => value,
            },
          },
          { key: 'fechaConstitucion', label: 'Fecha constitución', sortable: true, sortKey: 'FechaConstitucion', formatFn: (val) => this.formatDateValue(val) },
          { key: 'fechaFinalizacion', label: 'Fecha finalización', sortable: true, sortKey: 'FechaFinalizacion', formatFn: (val) => this.formatDateValue(val) },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Contabilizar (asiento libre)', icon: 'playlist_add', type: 'button', callback: () => this.addManualAccountMovements() },
        ],
        rowConfig: {},
      },
      {
        filterView: '',
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => {},
      onCollapse: () => {},
      onToggleExpand: () => {},
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.summaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const search = String((state.filters || {}).search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  onDetailAction(row: OwnFinancingRow, action: FinancingDetailAction): void {
    if (action.type === 'changed') this.load();
    if (action.type === 'payInstallments') this.payInstallments(row, action.editUid);
    if (action.type === 'constitution') this.constitution(row, action.editUid);
    if (action.type === 'shortTermDebt') this.shortTermDebt(row, action.editUid);
  }

  create(): void {
    if (!this.tier.requireAdvancedTier()) return;
    FinancingFormDialogComponent.open(this.dialog, { kind: 'own' })
      .subscribe(saved => { if (saved) this.load(); });
  }

  edit(row: OwnFinancingRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    FinancingFormDialogComponent.open(this.dialog, { kind: 'own', uid: row.uid })
      .subscribe(saved => { if (saved) this.load(); });
  }

  delete(row: OwnFinancingRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar financiación propia',
      subtitle: row.descripcion || undefined,
      message: 'Se eliminará la financiación junto con sus constituciones, pagos de cuotas, reclasificaciones a corto plazo y los asientos contables asociados. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.rowDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando financiación…',
      errorText: 'No se pudo eliminar la financiación',
      action: () => this.financiacionPropiaService.delete(row.uid).then(ok => this.ensureOk(ok)),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Financiación eliminada correctamente');
      this.load();
    });
  }

  uploadFileAttachment(row: OwnFinancingRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.dialog.open(FinanciacionPropiaDialogAttachmentComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { uid: row.uid, descripcion: row.descripcion },
    }).afterClosed().subscribe(changed => { if (changed === true) this.load(); });
  }

  downloadFileAttachment(row: OwnFinancingRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Descargando cuadro de amortización…', this.financiacionPropiaService.downloadFileAttachment(row.uid))
      .then(blob => FileSaver.saveAs(blob, Utils.GetFileName('Fichero_Adjunto', consts.EXT_EXCEL)))
      .catch(() => this.notify.error('No se pudo descargar el cuadro de amortización'));
  }

  constitution(row: OwnFinancingRow, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.openFormDialog(FinanciacionPropiaDialogConstitucionComponent, row, editUid);
  }

  shortTermDebt(row: OwnFinancingRow, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.openFormDialog(FinanciacionPropiaDialogDeudaACortoPlazoComponent, row, editUid);
  }

  payInstallments(row: OwnFinancingRow, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.openFormDialog(FinanciacionPropiaDialogPagoCuotasComponent, row, editUid);
  }

  addManualAccountMovements(): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.dialog.open(AccountingManualFormComponent, {
      width: '90vw',
      maxWidth: '96vw',
      height: '70%',
      data: { accountingMovementId: '' }
    }).afterClosed().subscribe((result: string) => {
      if (!Utils.IsNullOrEmpty(result) && result === 'OK') {
        this.load();
      }
    });
  }

  private openFormDialog(component: any, row: OwnFinancingRow, editUid?: string): void {
    this.dialog.open(component, {
      width: '760px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { financiacionPropia: row, editUid },
    }).afterClosed().subscribe(changed => { if (changed === true) this.load(); });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list({
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(item => item.__expanded).map(item => item.uid));
        const version = ++this.loadVersion;
        this.rows = result.items.map(item => ({
          ...item,
          estado: (item as any).estado || (isFinancingAmortized(item) ? 'Amortizada' : 'Pendiente'),
          __expanded: expanded.has(item.uid),
          __detailVersion: version,
        }));
        this.totals = {
          initial: Utils.RoundTwoDecimals(result.summary.initialAmount),
          pending: Utils.RoundTwoDecimals(result.summary.outstandingAmount),
          count: result.total,
        };
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de financiación propia');
      });
  }

  private rowDetails(row: OwnFinancingRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      ...(row.partnersName ? [{ label: 'Socio', value: row.partnersName }] : []),
      { label: 'Plazo', value: financingTermLabel(row.duracion) },
      { label: 'Importe inicial', value: Utils.FormatMoney(row.importeInicial ?? 0) + ' €' },
      { label: 'Importe pendiente', value: Utils.FormatMoney(row.importePendiente ?? 0) + ' €' },
    ];
    if (row.fechaConstitucion) details.push({ label: 'Fecha constitución', value: this.formatDateValue(row.fechaConstitucion) });
    return details;
  }

  private formatDateValue(value: any): string {
    return value ? formatDate(value, 'dd/MM/yyyy', 'es-ES') : '';
  }

  private ensureOk(ok: unknown): void {
    if (ok !== true) throw new Error();
  }
}
